import React, { useState, useEffect } from 'react';
import { UserCog, Building2, Users } from 'lucide-react';
import { Badge } from './ui/badge';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from './ui/popover';
import { Label } from './ui/label';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { toast } from 'sonner';
import { getRootDomain } from '../utils/sessionManager';
import { isDemoEnvironment } from './EnvironmentBadge';
import { cachedFetch } from '../utils/fetchWithCache';
import { getAuthHeaders } from '../utils/apiHeaders';
import { clearPersistedCompany } from './CompanyPicker';

import { createHandoffCode } from '../utils/sessionHandoff';
// --- ROLE DEFINITIONS ---
export const ROLES = {
  SYSTEM_ADMIN: 'systemAdmin',
  FIRM_ADMIN: 'cfAdmin',
  CONSULTANT: 'consultant',
  ENGAGEMENT_LEAD: 'Engagement Lead',
  ENGINEER: 'Engineer',
  PROJECT_LEAD: 'Project Lead',
  BILLING_ADMIN: 'billingAdmin',
  BD_REP: 'bdRep',
  COMPANY_ADMIN: 'companyAdmin',
  USER: 'user',
  LEADERSHIP_MARKETING: 'leadership_marketing',
} as const;

// --- ROLE GROUPING (presentation only — server controls visibility) ---
const CONSULTING_FIRM_ROLE_KEYS: string[] = [
  ROLES.SYSTEM_ADMIN,
  ROLES.FIRM_ADMIN,
  ROLES.ENGAGEMENT_LEAD,
  ROLES.CONSULTANT,
  ROLES.ENGINEER,
  ROLES.BD_REP,
  ROLES.BILLING_ADMIN,
  ROLES.LEADERSHIP_MARKETING,
];

const CLIENT_COMPANY_ROLE_KEYS: string[] = [
  ROLES.PROJECT_LEAD,
  ROLES.COMPANY_ADMIN,
  ROLES.USER,
];

interface RoleSwitcherProps {
  currentUser: any; // User object with roles array
  projectId: string;
  accessToken?: string;
  onRoleChange?: (newRole: string) => void;
}

const ROLE_LABELS: Record<string, { label: string; description: string }> = {
  [ROLES.USER]: { label: 'Client Team Member', description: 'Answer questions & view progress' },
  [ROLES.COMPANY_ADMIN]: { label: 'Client Engagement Lead', description: 'Manage company users & drive discovery' },
  [ROLES.CONSULTANT]: { label: 'Consultant', description: 'Engagement delivery' },
  [ROLES.BD_REP]: { label: 'Business Development Rep', description: 'Business development' },
  [ROLES.FIRM_ADMIN]: { label: 'Firm Admin', description: 'Manage consulting firm' },
  [ROLES.BILLING_ADMIN]: { label: 'Billing Admin', description: 'Manage billing' },
  [ROLES.SYSTEM_ADMIN]: { label: 'System Admin', description: 'Full system access' },
  [ROLES.LEADERSHIP_MARKETING]: { label: 'Leadership/Marketing', description: 'View success stories & timelines' },
  
  // Normalized mappings for legacy/alternate strings if needed (backend might return different strings)
  'consulting_firm_admin': { label: 'Firm Admin', description: 'Manage consulting firm' },
  'system_admin': { label: 'System Admin', description: 'Full system access' },
  'billing_admin': { label: 'Billing Admin', description: 'Manage billing' },
  'company_admin': { label: 'Client Engagement Lead', description: 'Manage company users & drive discovery' },
  'stakeholder': { label: 'Stakeholder', description: 'Client stakeholder' },
  
  // Per-Engagement Roles
  [ROLES.ENGAGEMENT_LEAD]: { label: 'Firm Engagement Lead', description: 'Owns delivery success & scope' },
  [ROLES.ENGINEER]: { label: 'Engineer', description: 'Technical implementation' },
  [ROLES.PROJECT_LEAD]: { label: 'Project Lead', description: 'Client-side project owner' },
};

export function RoleSwitcher({ currentUser, projectId, accessToken, onRoleChange }: RoleSwitcherProps) {
  const [open, setOpen] = useState(false);
  const [switching, setSwitching] = useState(false);
  
  // New state for server-side permission check
  const [canSwitchRole, setCanSwitchRole] = useState(false);
  const [permissionsLoaded, setPermissionsLoaded] = useState(false);
  const [allowedRolesFromServer, setAllowedRolesFromServer] = useState<string[]>([]);

  // Get user's roles - support both new roles array and legacy single role
  const userRoles = currentUser?.roles && Array.isArray(currentUser.roles) 
    ? currentUser.roles 
    : currentUser?.role 
      ? [currentUser.role] 
      : [];

  // Check if we're on a subdomain
  const hostname = window.location.hostname;
  const isSubdomain = hostname.includes('.pathkit.pro') && hostname !== 'pathkit.pro';

  // --- SERVER-SIDE GATING CHECK ---
  useEffect(() => {
    let mounted = true;

    async function checkPermissions() {
      // Short-circuit if no access token
      if (!accessToken) {
        if (mounted) setPermissionsLoaded(true);
        return;
      }

      try {
        const response = await cachedFetch(
          `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/auth/role-switch-permissions`,
          {
            headers: getAuthHeaders(accessToken),
          }
        );

        if (response.ok) {
          const data = await response.json();
          if (mounted) {
            setCanSwitchRole(data.canSwitchRole === true);
            if (data.allowedRoles && Array.isArray(data.allowedRoles)) {
              setAllowedRolesFromServer(data.allowedRoles);
            }
          }
        } else {
          console.warn('[RoleSwitcher] Failed to check permissions:', response.status);
          if (mounted) setCanSwitchRole(false);
        }
      } catch (error) {
        console.error('[RoleSwitcher] Error checking permissions:', error);
        if (mounted) setCanSwitchRole(false);
      } finally {
        if (mounted) setPermissionsLoaded(true);
      }
    }

    // Defer permissions check so the critical bundle / session calls get
    // connection priority during initial page load.
    const timer = setTimeout(checkPermissions, 1500);

    return () => { mounted = false; clearTimeout(timer); };
  }, [projectId, accessToken]);

  // Demo users should see ALL available roles for demo purposes
  const allDemoRoles = [
    ROLES.SYSTEM_ADMIN, 
    ROLES.FIRM_ADMIN, 
    ROLES.CONSULTANT, 
    ROLES.ENGAGEMENT_LEAD,
    ROLES.ENGINEER,
    ROLES.PROJECT_LEAD,
    ROLES.BILLING_ADMIN, 
    ROLES.BD_REP, 
    ROLES.COMPANY_ADMIN, 
    ROLES.USER,
    ROLES.LEADERSHIP_MARKETING
  ];
  
  const availableRoles = canSwitchRole
    ? (allowedRolesFromServer.length > 0 ? allowedRolesFromServer : allDemoRoles)
    : userRoles;

  // Get current active role
  const currentRole = currentUser?.activeRole || currentUser?.role || availableRoles[0] || 'user';
  
  // Only show role switcher if permissions loaded AND (user has multiple roles OR is allowed to switch)
  if (!permissionsLoaded || (availableRoles.length <= 1 && !canSwitchRole)) {
    return null;
  }
  
  // Debug helper: Check URL param for debug mode to show User ID for bootstrapping
  const isDemo = isDemoEnvironment();
  const showDebug = isDemo && window.location.search.includes('debug=1');

  const handleRoleChange = async (newRole: string) => {
    if (newRole === currentRole) {
      setOpen(false);
      return;
    }

    console.log('[RoleSwitcher] Starting role change:', {
      from: currentRole,
      to: newRole,
      isSubdomain,
      hostname,
      willRedirect: newRole === 'systemAdmin' && isSubdomain
    });

    setSwitching(true);
    let hasRedirected = false; // Track if we've initiated a redirect
    
    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/switch-active-role`,
        {
          method: 'POST',
          headers: {
            ...getAuthHeaders(accessToken || ''),
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            userId: currentUser.id,
            activeRole: newRole,
          }),
        }
      );

      console.log('[RoleSwitcher] Server response:', response.status, response.ok);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('Role switch failed:', response.status, errorData);
        throw new Error(errorData.error || 'Failed to switch role');
      }

      console.log('[RoleSwitcher] ✅ Role successfully changed in backend to:', newRole);
      console.log('[RoleSwitcher] Now evaluating redirect conditions...');
      console.log('[ROLE_SWITCH_FLOW] switch request succeeded, returned activeRole:', newRole);

      // Parse response payload for diagnostics
      const responsePayload = await response.clone().json().catch(() => ({}));
      console.log('[ROLE_SWITCH_FLOW] switch response payload:', JSON.stringify(responsePayload));

      // CRITICAL: Update localStorage immediately with the new role BEFORE redirecting
      // This prevents the infinite loop where old session data conflicts with new role
      const currentStoredUser = localStorage.getItem('pathkit_user');
      if (currentStoredUser) {
        try {
          const userData = JSON.parse(currentStoredUser);
          userData.activeRole = newRole;
          userData.role = newRole; // Update legacy field too
          // Preserve isAdmin flag for system admins switching roles
          // (don't clear it — the backend preserves it too)
          if (userData.isAdmin && newRole !== 'systemAdmin') {
            userData.baseRole = userData.baseRole || 'systemAdmin';
          }
          
          // SYSTEM ADMIN VIEW-AS MODE: When a system admin switches to a firm role,
          // do NOT auto-bind firm context. Clear it so FirmContextGate will prompt
          // the user to explicitly choose which firm to operate as.
          const FIRM_ROLES = ['cfAdmin', 'consultant', 'billingAdmin', 'bdRep', 'Engagement Lead', 'Engineer', 'leadership_marketing'];
          const isSystemAdminViewAs = userData.isAdmin && FIRM_ROLES.includes(newRole);

          if (isSystemAdminViewAs) {
            console.log('[VIEW_AS_CONTEXT] role switch initiated');
            console.log('[VIEW_AS_CONTEXT] system admin switching to', newRole, '— clearing firm context to force explicit selection');
            // Explicitly clear firm context so FirmContextGate will show the picker
            delete userData.firmId;
            delete userData.consultingFirmId;
            delete userData.firmName;
            // Mark this as a view-as session so FirmContextGate knows
            userData.viewAsMode = true;
          } else if (responsePayload?.firmContext?.firmId) {
            // Native firm user: auto-bind firm from response
            userData.firmId = responsePayload.firmContext.firmId;
            userData.consultingFirmId = responsePayload.firmContext.firmId;
            userData.firmName = responsePayload.firmContext.firmName;
            console.log('[CF_ADMIN_CONTEXT] Firm context from switch response (native user):', {
              firmId: responsePayload.firmContext.firmId,
              firmName: responsePayload.firmContext.firmName,
              source: responsePayload.firmContext.source,
              multipleFirms: responsePayload.firmContext.multipleFirmsAvailable,
            });
          } else if (responsePayload?.user?.firmId) {
            // Fallback: read from user object in response
            userData.firmId = responsePayload.user.firmId;
            userData.consultingFirmId = responsePayload.user.consultingFirmId || responsePayload.user.firmId;
            userData.firmName = responsePayload.user.firmName;
            console.log('[CF_ADMIN_CONTEXT] Firm context from user object:', {
              firmId: responsePayload.user.firmId,
              firmName: responsePayload.user.firmName,
            });
          } else {
            // No firm context in response — FirmContextGate will resolve on next load
            if (FIRM_ROLES.includes(newRole)) {
              console.warn('[CF_ADMIN_CONTEXT] switch response has NO firm context for firm role:', newRole);
              console.warn('[CF_ADMIN_CONTEXT] FirmContextGate will resolve on next page load');
            }
          }
          
          // CLIENT ROLE CONTEXT: When switching to a client role as a system admin,
          // clear persisted company so the CompanyPicker prompts for selection.
          const CLIENT_ROLES = ['companyAdmin', 'user', 'Project Lead'];
          const isSystemAdminClientViewAs =
            userData.isAdmin && CLIENT_ROLES.includes(newRole);
          if (isSystemAdminClientViewAs) {
            console.log('[VIEW_AS_CONTEXT] system admin switching to client role', newRole, '— clearing company context');
            delete userData.companyId;
            delete userData.companyName;
            clearPersistedCompany();
          }

          // VIEW-AS URL HYGIENE: when a system admin enters view-as mode
          // (firm OR client), drop the `/system-admin` URL so the page no
          // longer claims to be the System Admin panel. AppRouter's
          // renderViewForActiveRole handles `/` for view-as users by
          // rendering the role-appropriate experience (FirmContextGate or
          // CompanyPicker as needed). Switching BACK to systemAdmin is
          // unaffected — the root-redirect in AppRouter sends the operator
          // to /system-admin again automatically.
          if (
            userData.isAdmin &&
            (isSystemAdminViewAs || isSystemAdminClientViewAs) &&
            window.location.pathname.startsWith('/system-admin')
          ) {
            console.log(
              '[VIEW_AS_CONTEXT] navigating off /system-admin → / for',
              newRole,
              'view-as session',
            );
            window.history.replaceState({}, '', '/');
          }
          
          localStorage.setItem('pathkit_user', JSON.stringify(userData));
          console.log('[RoleSwitcher] ✅ Updated localStorage with new role before redirect');
          console.log('[ROLE_SWITCH_FLOW] session cache updated: role=' + newRole + ', isAdmin=' + userData.isAdmin + ', baseRole=' + userData.baseRole);
          console.log('[CF_ADMIN_CONTEXT] role=' + newRole + ', firmId=' + userData.firmId + ', firmName=' + userData.firmName);
        } catch (e) {
          console.warn('[RoleSwitcher] Could not update localStorage:', e);
        }
      }

      // If switching to System Admin from a subdomain, redirect to root domain IMMEDIATELY
      // Do this BEFORE showing toast or anything else
      if (newRole === 'systemAdmin' && isSubdomain) {
        // STORE THE CURRENT SUBDOMAIN so we can return to it later
        const currentSubdomain = hostname.split('.')[0];
        console.log('[RoleSwitcher] Storing subdomain for return journey:', currentSubdomain);
        sessionStorage.setItem('pathkit_return_subdomain', currentSubdomain);
        
        const rootDomain = getRootDomain();
        console.log('[RoleSwitcher] ✅ Role switched in DB. Now redirecting to root domain...');
        console.log('[RoleSwitcher] Root domain for this environment:', rootDomain);
        console.log('[RoleSwitcher] accessToken being passed:', accessToken ? `${accessToken.substring(0, 20)}...` : 'NULL/UNDEFINED');
        // Pass the access token in the URL so the root domain can establish a session
        const protocol = window.location.protocol;
        // One-time handoff code instead of the session token in the URL.
        const code = await createHandoffCode(accessToken || '');
        const redirectUrl = `${protocol}//${rootDomain}/system-admin?handoff=${encodeURIComponent(code)}`;
        console.log('[RoleSwitcher] Target URL:', redirectUrl);
        console.log('[RoleSwitcher] Executing redirect now...');
        // Don't show toast, don't reload, just redirect immediately
        window.location.href = redirectUrl;
        hasRedirected = true; // Mark that we've redirected
        return;
      }

      // If switching FROM System Admin to another role (like cfAdmin, consultant, etc.)
      // and we're on the root domain, DO NOT redirect to a subdomain.
      // System admins testing other roles should stay on root domain.
      // The AppRouter will re-render the appropriate experience based on activeRole.
      const isOnRootDomain = hostname === 'pathkit.pro' || 
                             hostname === 'www.pathkit.pro' ||
                             hostname === 'staging.pathkit.pro' || 
                             hostname === 'localhost' || 
                             hostname.startsWith('localhost:');
      
      console.log('[RoleSwitcher] Checking redirect conditions:', {
        currentRole,
        newRole,
        isOnRootDomain,
        hostname,
        userSubdomain: currentUser?.subdomain,
        userConsultingFirmSubdomain: currentUser?.consultingFirmSubdomain,
        allUserKeys: Object.keys(currentUser || {})
      });
      
      if (currentRole === 'systemAdmin' && newRole !== 'systemAdmin' && isOnRootDomain) {
        // STAY ON ROOT DOMAIN — do NOT redirect to subdomain.
        // System admins switching roles for testing should remain on the current domain.
        // The page will reload and AppRouter will render the appropriate view based on activeRole.
        console.log('[RoleSwitcher] System admin switching to', newRole, 'on root domain — staying in-place (no subdomain redirect)');
        console.log('[ROLE_SWITCH_FLOW] root domain self-switch, will reload in-place');
      }
      
      if (!hasRedirected) {
        console.log('[RoleSwitcher] No redirect needed, reloading page...');
        console.log('[ROLE_SWITCH_FLOW] reload target URL:', window.location.href);
        console.log('[ROLE_SWITCH_FLOW] activeRole after switch:', newRole);
        console.log('[ROLE_SWITCH_FLOW] firmId present:', !!(currentUser?.firmId || currentUser?.consultingFirmId));
        console.log('[ROLE_SWITCH_FLOW] tenant/subdomain present:', !!(currentUser?.subdomain || isSubdomain));
        const roleLabel = ROLE_LABELS[newRole]?.label || newRole;
        toast.success(`Switched to ${roleLabel} view`);
        setOpen(false);
        
        // Notify parent component to refresh
        if (onRoleChange) {
          onRoleChange(newRole);
        } else {
          // Reload the page to apply new role view
          window.location.reload();
        }
      }
    } catch (error) {
      console.error('Error switching role:', error);
      toast.error('Failed to switch role');
    } finally {
      setSwitching(false);
    }
  };

  const currentRoleData = ROLE_LABELS[currentRole] || { label: currentRole, description: 'Unknown role' };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button 
          className="relative p-2 rounded-md hover:bg-gray-100 transition-colors"
          title="Switch Role"
        >
          <UserCog className="h-5 w-5 text-gray-600" />
          <span className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-blue-600 flex items-center justify-center">
            <span className="text-[10px] text-white font-bold">{availableRoles.length}</span>
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="end">
        <div className="border-b p-4 bg-blue-50">
          <div className="flex items-center gap-2 mb-2">
            <UserCog className="h-4 w-4 text-blue-600" />
            <h3 className="font-medium">{canSwitchRole ? 'View As (Testing)' : 'Switch Role'}</h3>
            <Badge variant="secondary" className="ml-auto bg-blue-600 text-white text-xs">
              {availableRoles.length} Roles
            </Badge>
          </div>
          <p className="text-xs text-muted-foreground">
            {canSwitchRole
              ? 'Preview the platform as different roles.' 
              : 'Switch between your assigned roles.'}
          </p>
          {currentRoleData && (
            <div className="mt-2">
              <p className="text-xs text-blue-700">
                Current: <span className="font-medium">{currentRoleData.label}</span>
              </p>
            </div>
          )}
        </div>

        <div className="max-h-[60vh] overflow-y-auto">
          <RadioGroup 
            value={currentRole} 
            onValueChange={handleRoleChange}
            disabled={switching}
          >
            {/* --- CONSULTING FIRM SECTION --- */}
            {(() => {
              const firmRoles = CONSULTING_FIRM_ROLE_KEYS.filter(r => availableRoles.includes(r));
              if (firmRoles.length === 0) return null;
              return (
                <div className="px-4 pt-4 pb-2">
                  <div className="flex items-center gap-2 mb-3">
                    <Building2 className="h-3.5 w-3.5 text-indigo-500" />
                    <span className="text-[11px] font-semibold text-indigo-600 uppercase tracking-wider">Consulting Firm</span>
                    <div className="flex-1 h-px bg-indigo-100 ml-1" />
                  </div>
                  <div className="space-y-1.5">
                    {firmRoles.map((roleValue) => {
                      const roleInfo = ROLE_LABELS[roleValue] || { label: roleValue, description: 'Custom role' };
                      const isActive = currentRole === roleValue;
                      return (
                        <div
                          key={roleValue}
                          className={`flex items-center gap-3 px-3 py-2.5 rounded-lg border transition-colors ${
                            isActive
                              ? 'bg-indigo-50 border-indigo-300 ring-1 ring-indigo-200'
                              : 'border-transparent hover:bg-gray-50 hover:border-gray-200'
                          } ${switching ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                          onClick={() => !switching && handleRoleChange(roleValue)}
                        >
                          <RadioGroupItem 
                            value={roleValue} 
                            id={`role-${roleValue}`}
                            disabled={switching}
                          />
                          <div className="flex-1 min-w-0">
                            <Label
                              htmlFor={`role-${roleValue}`}
                              className="cursor-pointer font-medium text-sm leading-none"
                            >
                              {roleInfo.label}
                              {isActive && (
                                <Badge variant="secondary" className="ml-2 text-[10px] px-1.5 py-0 bg-indigo-100 text-indigo-700 border-indigo-200">
                                  Active
                                </Badge>
                              )}
                            </Label>
                            <p className="text-[11px] text-muted-foreground mt-0.5 leading-tight">
                              {roleInfo.description}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })()}

            {/* --- CLIENT COMPANY SECTION --- */}
            {(() => {
              const clientRoles = CLIENT_COMPANY_ROLE_KEYS.filter(r => availableRoles.includes(r));
              if (clientRoles.length === 0) return null;
              return (
                <div className="px-4 pt-3 pb-3">
                  <div className="flex items-center gap-2 mb-3">
                    <Users className="h-3.5 w-3.5 text-emerald-500" />
                    <span className="text-[11px] font-semibold text-emerald-600 uppercase tracking-wider">Client Company</span>
                    <div className="flex-1 h-px bg-emerald-100 ml-1" />
                  </div>
                  <div className="space-y-1.5">
                    {clientRoles.map((roleValue) => {
                      const roleInfo = ROLE_LABELS[roleValue] || { label: roleValue, description: 'Custom role' };
                      const isActive = currentRole === roleValue;
                      return (
                        <div
                          key={roleValue}
                          className={`flex items-center gap-3 px-3 py-2.5 rounded-lg border transition-colors ${
                            isActive
                              ? 'bg-emerald-50 border-emerald-300 ring-1 ring-emerald-200'
                              : 'border-transparent hover:bg-gray-50 hover:border-gray-200'
                          } ${switching ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                          onClick={() => !switching && handleRoleChange(roleValue)}
                        >
                          <RadioGroupItem 
                            value={roleValue} 
                            id={`role-${roleValue}`}
                            disabled={switching}
                          />
                          <div className="flex-1 min-w-0">
                            <Label
                              htmlFor={`role-${roleValue}`}
                              className="cursor-pointer font-medium text-sm leading-none"
                            >
                              {roleInfo.label}
                              {isActive && (
                                <Badge variant="secondary" className="ml-2 text-[10px] px-1.5 py-0 bg-emerald-100 text-emerald-700 border-emerald-200">
                                  Active
                                </Badge>
                              )}
                            </Label>
                            <p className="text-[11px] text-muted-foreground mt-0.5 leading-tight">
                              {roleInfo.description}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })()}

            {/* --- UNGROUPED ROLES (fallback for any role not in either group) --- */}
            {(() => {
              const allGrouped = new Set([...CONSULTING_FIRM_ROLE_KEYS, ...CLIENT_COMPANY_ROLE_KEYS]);
              const ungrouped = availableRoles.filter(r => !allGrouped.has(r));
              if (ungrouped.length === 0) return null;
              return (
                <div className="px-4 pt-2 pb-3">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Other</span>
                    <div className="flex-1 h-px bg-gray-200 ml-1" />
                  </div>
                  <div className="space-y-1.5">
                    {ungrouped.map((roleValue) => {
                      const roleInfo = ROLE_LABELS[roleValue] || { label: roleValue, description: 'Custom role' };
                      const isActive = currentRole === roleValue;
                      return (
                        <div
                          key={roleValue}
                          className={`flex items-center gap-3 px-3 py-2.5 rounded-lg border transition-colors ${
                            isActive
                              ? 'bg-blue-50 border-blue-300 ring-1 ring-blue-200'
                              : 'border-transparent hover:bg-gray-50 hover:border-gray-200'
                          } ${switching ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                          onClick={() => !switching && handleRoleChange(roleValue)}
                        >
                          <RadioGroupItem 
                            value={roleValue} 
                            id={`role-${roleValue}`}
                            disabled={switching}
                          />
                          <div className="flex-1 min-w-0">
                            <Label
                              htmlFor={`role-${roleValue}`}
                              className="cursor-pointer font-medium text-sm leading-none"
                            >
                              {roleInfo.label}
                              {isActive && (
                                <Badge variant="secondary" className="ml-2 text-[10px] px-1.5 py-0">
                                  Active
                                </Badge>
                              )}
                            </Label>
                            <p className="text-[11px] text-muted-foreground mt-0.5 leading-tight">
                              {roleInfo.description}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })()}
          </RadioGroup>
        </div>

        <div className="border-t p-3 bg-gray-50">
          <p className="text-xs text-muted-foreground text-center">
            Permissions change based on the active role
          </p>
          {showDebug && (
            <div className="mt-2 pt-2 border-t border-gray-100 text-[10px] text-gray-400 font-mono text-center">
              User ID: {currentUser.id}<br/>
              Add to DEMO_ALLOWLIST_USER_IDS
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}